import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, AlertTriangle, ShoppingCart, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

/**
 * Compact comparator that appears on DIN-3567 product detail pages.
 * Shows the current product (H = single bracket OR P = full set) side-by-side
 * with the complementary family so customers can switch in one click.
 */
export default function HpComparator({ product, dnContext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(null);
  const [bulkFlash, setBulkFlash] = useState(null);
  const [qtyByKey, setQtyByKey] = useState({});   // { 'H-304H-7997-½"': 5, 'P-...': 10 }
  const { addItem } = useCart();
  const isDinHalfOrSet = product?.kit_type === "din3567-half" || product?.kit_type === "din3567-set";

  useEffect(() => {
    if (!product || !isDinHalfOrSet) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .get(`/products/${product.id}/h-p-counterpart`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [product, isDinHalfOrSet]);

  if (!isDinHalfOrSet || loading || !data?.counterpart) return null;

  const cp = data.counterpart;
  const dnQuery = dnContext ? `?dn=${dnContext}` : "";
  const cpUrl = `/products/${cp.id}${dnQuery}`;
  const currentIsHalf = data.current_letter === "H";

  // Same shape, two columns: current (left) + counterpart (right)
  const Card = ({ name, sku, priceFrom, priceTo, isHalf, current, href }) => {
    const Icon = isHalf ? AlertTriangle : CheckCircle2;
    return (
      <div
        className={`flex flex-1 flex-col rounded-md border-2 p-4 transition ${
          current
            ? "border-nibro-sage-dark bg-nibro-sage-pale"
            : isHalf
            ? "border-amber-300 bg-amber-50 hover:border-amber-500"
            : "border-emerald-300 bg-emerald-50 hover:border-emerald-500"
        }`}
        data-testid={`hp-card-${current ? "current" : "counterpart"}`}
      >
        <div className="mb-2 flex items-center gap-2">
          <Icon size={14} className={isHalf ? "text-amber-700" : "text-emerald-700"} />
          <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-zinc-600">
            {isHalf ? "Letter H — enkele beugel" : "Letter P — volledige set"}
          </span>
        </div>
        <div className="font-display text-sm font-bold leading-tight text-zinc-900">
          {isHalf ? "Enkele beugel" : "Set met bouten + moeren"}
        </div>
        <div className="mt-0.5 font-mono text-[11px] text-zinc-500">{sku}</div>
        <div className="mt-2 font-display text-lg font-bold text-zinc-900">
          €{priceFrom?.toFixed(2)}{priceTo > priceFrom ? ` – €${priceTo?.toFixed(2)}` : ""}
        </div>
        <div className="text-[11px] text-zinc-500">
          {isHalf ? "Alleen 1 helft · geen bouten" : "Boven + onder + bouten + moeren"}
        </div>
        {!current && href && (
          <Link
            to={href}
            data-testid="hp-switch-link"
            className={`mt-3 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold uppercase tracking-wide transition ${
              isHalf
                ? "bg-amber-600 text-white hover:bg-amber-700"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
          >
            Bekijk {isHalf ? "enkele beugel" : "volledige set"} <ArrowRight size={12} />
          </Link>
        )}
        {current && (
          <div className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md bg-nibro-sage-dark px-3 py-2 text-xs font-bold uppercase tracking-wide text-white">
            Huidige selectie
          </div>
        )}
      </div>
    );
  };

  const currentSku = (product.variants || [])[0]?.sku || product.sku;

  // Build paired rows by DN (or diameter_mm fallback) for the side-by-side table
  const currentVariants = product?.variants || [];
  const cpVariants = cp.variants || [];
  const keyOf = (v) => v?.dn ?? `Ø${v?.diameter_mm}`;
  const cpByKey = new Map(cpVariants.map((v) => [keyOf(v), v]));
  const curByKey = new Map(currentVariants.map((v) => [keyOf(v), v]));
  const allKeys = Array.from(new Set([...currentVariants.map(keyOf), ...cpVariants.map(keyOf)]));
  const pairedRows = allKeys
    .map((k) => ({ key: k, half: currentIsHalf ? curByKey.get(k) : cpByKey.get(k), set: currentIsHalf ? cpByKey.get(k) : curByKey.get(k) }))
    .filter((r) => r.half || r.set)
    .sort((a, b) => {
      const da = (a.half?.diameter_mm ?? a.set?.diameter_mm) || 0;
      const db = (b.half?.diameter_mm ?? b.set?.diameter_mm) || 0;
      return da - db;
    });

  const halfProduct = currentIsHalf ? product : data?.counterpart;
  const setProduct = currentIsHalf ? data?.counterpart : product;

  const addVariantToCart = (variant, parentProduct, kind, qty = 1) => {
    if (!variant || !parentProduct) return;
    addItem({
      product_id: parentProduct.id,
      sku: variant.sku,
      name: parentProduct.name_nl || parentProduct.name,
      image: parentProduct.image,
      quantity: qty,
      unit_price: variant.price,
      meta: { dn: variant.dn, diameter_mm: variant.diameter_mm, kind, weight_g: variant.weight_g },
    });
    setJustAdded(`${kind}-${variant.sku}`);
    setTimeout(() => setJustAdded(null), 1400);
  };

  const setRowQty = (key, value) => {
    const n = Math.max(1, parseInt(value, 10) || 1);
    setQtyByKey((prev) => ({ ...prev, [key]: n }));
  };
  const getRowQty = (key) => qtyByKey[key] || 1;

  const addAll = (kind) => {
    const variants = kind === "H" ? (halfProduct?.variants || []) : (setProduct?.variants || []);
    const parent = kind === "H" ? halfProduct : setProduct;
    variants.forEach((v) => {
      const key = `${kind}-${v.sku}`;
      addVariantToCart(v, parent, kind, getRowQty(key));
    });
    setBulkFlash(kind);
    setTimeout(() => setBulkFlash(null), 1800);
  };

  return (
    <div
      className="mt-6 rounded-md border border-zinc-200 bg-white p-4"
      data-testid="hp-comparator"
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-zinc-500">
          H vs P · Artikelnummer indicator
        </span>
      </div>
      <p className="mb-4 text-xs text-zinc-600">
        Het artikelnummer onthult of u een <strong>enkele beugel</strong> (letter <span className="font-mono font-bold">H</span>) of een <strong>volledige set</strong> (letter <span className="font-mono font-bold">P</span>) bekijkt — kies bewust om misgrijpen op de werkvloer te voorkomen.
      </p>
      <div className="flex flex-col gap-3 sm:flex-row">
        {currentIsHalf ? (
          <>
            <Card
              current
              isHalf={true}
              name={product.name_nl}
              sku={currentSku}
              priceFrom={product.price_from}
              priceTo={product.price_to}
            />
            <Card
              isHalf={false}
              name={cp.name_nl}
              sku={cp.first_sku}
              priceFrom={cp.price_from}
              priceTo={cp.price_to}
              href={cpUrl}
            />
          </>
        ) : (
          <>
            <Card
              isHalf={true}
              name={cp.name_nl}
              sku={cp.first_sku}
              priceFrom={cp.price_from}
              priceTo={cp.price_to}
              href={cpUrl}
            />
            <Card
              current
              isHalf={false}
              name={product.name_nl}
              sku={currentSku}
              priceFrom={product.price_from}
              priceTo={product.price_to}
            />
          </>
        )}
      </div>

      {/* Side-by-side DN/diameter pricing table */}
      {pairedRows.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-md border border-zinc-200">
          <button
            type="button"
            onClick={() => setTableOpen((o) => !o)}
            data-testid="hp-table-toggle"
            className="flex w-full items-center justify-between gap-2 bg-zinc-50 px-4 py-2.5 text-left text-sm font-semibold text-zinc-800 transition hover:bg-zinc-100"
            aria-expanded={tableOpen}
          >
            <span>
              Bekijk alle {pairedRows.length} maten naast elkaar — direct toevoegen aan winkelwagen
            </span>
            {tableOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {tableOpen && (
            <div data-testid="hp-table-body">
              {/* Bulk-add bar */}
              <div className="flex flex-col gap-2 border-t border-zinc-200 bg-white px-4 py-3 text-xs sm:flex-row sm:items-center sm:justify-between">
                <span className="text-zinc-600">
                  Tip: vul per rij een aantal in en voeg de hele kolom in één klik toe.
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => addAll("H")}
                    data-testid="hp-bulk-add-h"
                    className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 font-bold uppercase tracking-wide transition ${
                      bulkFlash === "H" ? "bg-emerald-600 text-white" : "bg-amber-100 text-amber-900 hover:bg-amber-200"
                    }`}
                  >
                    {bulkFlash === "H" ? <><CheckCircle2 size={11} /> Alle H toegevoegd</> : <>+ Alle H ({pairedRows.filter(r => r.half).length} maten)</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => addAll("P")}
                    data-testid="hp-bulk-add-p"
                    className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 font-bold uppercase tracking-wide transition ${
                      bulkFlash === "P" ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200"
                    }`}
                  >
                    {bulkFlash === "P" ? <><CheckCircle2 size={11} /> Alle P toegevoegd</> : <>+ Alle P ({pairedRows.filter(r => r.set).length} maten)</>}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-t border-zinc-200 text-sm">
                <thead className="bg-white">
                  <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                    <th className="px-3 py-2">Maat</th>
                    <th className="px-3 py-2 text-amber-700">H · Enkele beugel</th>
                    <th className="px-3 py-2 text-emerald-700">P · Volledige set</th>
                  </tr>
                </thead>
                <tbody>
                  {pairedRows.map(({ key, half, set }) => {
                    const QtyStepper = ({ kind, sku }) => {
                      const k = `${kind}-${sku}`;
                      const q = getRowQty(k);
                      return (
                        <div className="mt-1 inline-flex overflow-hidden rounded border border-zinc-200">
                          <button
                            type="button"
                            onClick={() => setRowQty(k, q - 1)}
                            data-testid={`hp-qty-minus-${kind}-${sku}`}
                            className="px-1.5 text-zinc-500 hover:bg-zinc-100"
                            aria-label="Minder"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={q}
                            onChange={(e) => setRowQty(k, e.target.value)}
                            data-testid={`hp-qty-input-${kind}-${sku}`}
                            className="w-10 bg-transparent text-center font-mono text-[11px] focus:outline-none"
                            aria-label="Aantal"
                          />
                          <button
                            type="button"
                            onClick={() => setRowQty(k, q + 1)}
                            data-testid={`hp-qty-plus-${kind}-${sku}`}
                            className="px-1.5 text-zinc-500 hover:bg-zinc-100"
                            aria-label="Meer"
                          >
                            +
                          </button>
                        </div>
                      );
                    };
                    return (
                    <tr key={key} className="border-t border-zinc-100" data-testid={`hp-table-row-${key}`}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-display text-sm font-bold text-zinc-900">
                          {half?.dn || set?.dn ? `DN${half?.dn || set?.dn}` : ""}
                        </div>
                        <div className="font-mono text-[11px] text-zinc-500">
                          Ø{half?.diameter_mm || set?.diameter_mm} mm{(half?.inch || set?.inch) ? ` · ${half?.inch || set?.inch}` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        {half ? (
                          <div>
                            <div className="font-mono text-[11px] text-zinc-500">{half.sku}</div>
                            <div className="font-display text-base font-bold text-zinc-900">€{half.price?.toFixed(2)}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <QtyStepper kind="H" sku={half.sku} />
                              <button
                                type="button"
                                onClick={() => addVariantToCart(half, halfProduct, "H", getRowQty(`H-${half.sku}`))}
                                disabled={justAdded === `H-${half.sku}`}
                                data-testid={`hp-add-h-${half.sku}`}
                                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-bold uppercase tracking-wide transition ${
                                  justAdded === `H-${half.sku}`
                                    ? "bg-emerald-600 text-white"
                                    : "bg-amber-100 text-amber-900 hover:bg-amber-200"
                                }`}
                              >
                                {justAdded === `H-${half.sku}` ? (
                                  <><CheckCircle2 size={11} /> Toegevoegd</>
                                ) : (
                                  <><ShoppingCart size={11} /> In korf</>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] italic text-zinc-400">niet beschikbaar</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {set ? (
                          <div>
                            <div className="font-mono text-[11px] text-zinc-500">{set.sku}</div>
                            <div className="font-display text-base font-bold text-zinc-900">€{set.price?.toFixed(2)}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <QtyStepper kind="P" sku={set.sku} />
                              <button
                                type="button"
                                onClick={() => addVariantToCart(set, setProduct, "P", getRowQty(`P-${set.sku}`))}
                                disabled={justAdded === `P-${set.sku}`}
                                data-testid={`hp-add-p-${set.sku}`}
                                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-bold uppercase tracking-wide transition ${
                                  justAdded === `P-${set.sku}`
                                    ? "bg-emerald-600 text-white"
                                    : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200"
                                }`}
                              >
                                {justAdded === `P-${set.sku}` ? (
                                  <><CheckCircle2 size={11} /> Toegevoegd</>
                                ) : (
                                  <><ShoppingCart size={11} /> In korf</>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] italic text-zinc-400">niet beschikbaar</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
